import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { remitosApi } from '../api/remitos'
import EstadoVigenteBadge from '../components/EstadoVigenteBadge'
import EstadoChipsFilter from '../components/EstadoChipsFilter'
import { fmtFecha } from '../utils/fecha'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import useDebounce from '../hooks/useDebounce'
import Boton from '../components/Boton'
import { colors, font, text, space, radius } from '../design/tokens'
import Paginador from '../components/Paginador'

// Estados canónicos del estado_vigente (única fuente de verdad, del backend).
// El filtro viaja a la API (estado_vigente=a,b) y el backend filtra TODO el
// universo antes de paginar — el front NO deriva ni filtra. (Hasta el 31 oct
// 2026 se filtraba acá sobre los 30 de la página: el filtro no traía todo.)
// Ver docs/auditoria-estados-entrega.md.
const ESTADO_OPCIONES = [
  { value: 'ingresado',           label: 'Ingresado',     bg: '#fef3c7', color: '#92400e' },
  { value: 'disponible',          label: 'Disponible',    bg: '#dbeafe', color: '#1e40af' },
  { value: 'en_armado',           label: 'En armado',     bg: '#fef3c7', color: '#92400e' },
  { value: 'en_calle',            label: 'En distribución', bg: '#ede9fe', color: '#5b21b6' },
  { value: 'entregado',           label: 'Entregado',     bg: '#dcfce7', color: '#166534' },
  { value: 'recuperado',          label: 'Recuperado',    bg: '#dcfce7', color: '#166534' },
  { value: 'fallo_sin_reasignar', label: 'Sin reasignar', bg: '#fee2e2', color: '#991b1b' },
  { value: 'falta_despachar',     label: 'Falta despachar', bg: '#fef3c7', color: '#92400e' },
  { value: 'siniestro',           label: 'Siniestro',     bg: '#fee2e2', color: '#991b1b' },
  { value: 'devolucion_en_camino', label: 'Devolución en camino', bg: '#fee2e2', color: '#991b1b' },
  { value: 'devuelto_deposito', label: 'Devuelto al depósito', bg: '#f1f5f9', color: '#334155' },
  { value: 'anulado',             label: 'Anulado',       bg: '#fecaca', color: '#7f1d1d' },
]

const TRANSPORTE_OPCIONES = [
  { value: '80', label: '80 Virtual', bg: '#ede9fe', color: '#5b21b6' },
  { value: '90', label: '90 Físico',  bg: '#fef3c7', color: '#92400e' },
]

const FILTROS_DEFAULT = { q: '', estados: [], transportes: [], fecha_desde: '', fecha_hasta: '' }

export default function RemitosPage() {
  const navigate = useNavigate()
  const [remitos, setRemitos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useStoredState('lgr.filtros.remitos', FILTROS_DEFAULT)
  const [paginacion, setPaginacion] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [page, setPage] = useState(1)

  // Debounce del buscador (350 ms) — sin esto cada tecla era un request.
  const qBuscar = useDebounce(filtros.q, 350)

  const cargar = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      // El estado viaja como estado_vigente (el código canónico que se ve en
      // pantalla, no el escalar raw): el backend filtra todo y después pagina.
      const params = {
        page,
        ...(filtros.estados.length ? { estado_vigente: filtros.estados.join(',') } : {}),
        ...(qBuscar ? { q: qBuscar } : {}),
        ...(filtros.fecha_desde ? { fecha_desde: filtros.fecha_desde } : {}),
        ...(filtros.fecha_hasta ? { fecha_hasta: filtros.fecha_hasta } : {}),
        ...(filtros.transportes.length === 1 ? { transporte: filtros.transportes[0] } : {}),
      }
      const { data } = await remitosApi.listar(params)
      setRemitos(data.data)
      setPaginacion({ current_page: data.current_page, last_page: data.last_page, total: data.total })
      setError(null)
    } catch {
      setError('No se pudieron cargar los remitos. Reintentá o limpiá los filtros.')
    } finally { if (!silent) setLoading(false) }
    // filtros.q NO es dependencia: el fetch usa qBuscar (debounced)
  }, [qBuscar, filtros.fecha_desde, filtros.fecha_hasta, filtros.transportes, filtros.estados, page])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  const setFiltro = (k) => (e) => { setFiltros(f => ({ ...f, [k]: e.target.value })); setPage(1) }
  const toggle = (campo) => (val) => {
    setFiltros(f => {
      const arr = f[campo]
      const next = arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]
      return { ...f, [campo]: next }
    })
    setPage(1)
  }
  const limpiar = () => { setFiltros(FILTROS_DEFAULT); setPage(1) }

  const hayFiltros = Boolean(
    filtros.q || filtros.fecha_desde || filtros.fecha_hasta || filtros.estados.length > 0 || filtros.transportes.length > 0
  )

  // El filtro por estado ya vino aplicado del backend (estado_vigente).
  const remitosVisibles = remitos

  return (
    <div>
      <div style={st.header}>
        <h2 style={st.title}>Remitos</h2>
        {/* "+ Nuevo remito" (remito libre) se sacó (Martin, 8 ago): su método
            WS (generar_remito) nunca existió en Presea — el reemplazo es el
            modo "Solo remito" del modal de la NV, que sí despacha de verdad. */}
      </div>

      <div style={st.filtros}>
        <input style={st.fi} placeholder="Buscar nº o cliente..." value={filtros.q} onChange={setFiltro('q')} />
        <input style={st.fi} type="date" value={filtros.fecha_desde} onChange={setFiltro('fecha_desde')} />
        <input style={st.fi} type="date" value={filtros.fecha_hasta} onChange={setFiltro('fecha_hasta')} />
        <Boton variante={hayFiltros ? 'secondary' : 'ghost'} onClick={limpiar}>Limpiar</Boton>
      </div>
      <EstadoChipsFilter options={ESTADO_OPCIONES} selected={filtros.estados} onToggle={toggle('estados')} />
      <EstadoChipsFilter options={TRANSPORTE_OPCIONES} selected={filtros.transportes} onToggle={toggle('transportes')} style={{ margin: '0 0 0.6rem' }} />

      {!loading && !error && (
        <p style={st.info}>
          {paginacion.total} registro{paginacion.total !== 1 ? 's' : ''}
          {hayFiltros && <strong style={st.filtrosActivos}> · filtros activos</strong>}
        </p>
      )}

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>
              {['Nº Presea', 'Fecha', 'Tipo', 'Código', 'Cliente', 'Zona', 'Estado'].map((h, i) => (
                <th key={i} style={st.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && remitosVisibles.length === 0 ? (
              <tr><td colSpan={7} style={st.empty}>Cargando...</td></tr>
            ) : error ? (
              <tr><td colSpan={7} style={st.empty}>{error}</td></tr>
            ) : remitosVisibles.length === 0 ? (
              <tr><td colSpan={7} style={st.empty}>
                {hayFiltros ? (
                  <>
                    <div>No hay remitos que coincidan con los filtros.</div>
                    <Boton variante="secondary" sm style={{ marginTop: space.md }} onClick={limpiar}>Limpiar filtros</Boton>
                  </>
                ) : 'Sin resultados.'}
              </td></tr>
            ) : remitosVisibles.map(r => (
              <tr key={r.id}
                onClick={() => navigate(`/remitos/${r.id}`)}
                onMouseEnter={e => { e.currentTarget.style.background = colors.bgSoft }}
                onMouseLeave={e => { e.currentTarget.style.background = '' }}
                style={{ ...st.tr, cursor: 'pointer' }}>
                <td style={st.tdMono}>
                  {r.numero_presea && r.numero_presea !== '0'
                    ? r.numero_presea
                    : <span style={st.placeholder}>—</span>}
                </td>
                <td style={st.tdFecha}>{fmtFecha(r.fecha_emision)}</td>
                <td style={st.td}>
                  <span style={{ ...st.badge, ...(r.transporte === 0 ? st.badgeVirtual : r.transporte === 80 ? st.badgeT80 : st.badgeT90) }}>
                    {r.transporte === 0 ? 'Virtual' : `T${r.transporte}`}
                  </span>
                </td>
                <td style={st.tdMono}>{r.cliente?.codigo_presea || '—'}</td>
                <td style={st.td}>
                  <span style={st.principal}>{r.cliente?.nombre}</span>
                </td>
                <td style={st.tdSec}>
                  {r.zona?.nombre || <span style={st.noZona}>Sin zona</span>}
                </td>
                <td style={st.td}>
                  <EstadoVigenteBadge estadoVigente={r.estado_vigente} />
                  {r.anulacion_solicitada_at && r.estado !== 'anulado' && (
                    <div style={st.subtitulo} title="Falta anularlo en Presea: LGR lo aprueba solo cuando Presea lo muestra anulado">
                      ⏳ Anulación pedida
                    </div>
                  )}
                  {r.transporte === 80 && r.notas_envio_count > 0 && (
                    <div style={st.subtitulo}>
                      {(r.ne_entregadas_count || 0)}/{r.notas_envio_count} NE entregadas
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginador pagina={page} totalPaginas={paginacion.last_page} onCambiar={setPage} />
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  filtros: { display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center', marginBottom: space.md, background: colors.bg, padding: space.lg, borderRadius: radius.md, border: `1px solid ${colors.border}` },
  fi: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', minWidth: 130, background: colors.bg },
  info: { margin: `0 0 ${space.md}px`, color: colors.textSec, fontSize: text.sec },
  filtrosActivos: { color: colors.link, fontWeight: 600 },

  tableWrap: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: '8px 16px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 44 },
  td: { padding: '0 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, lineHeight: 1.2 },
  tdMono: { padding: '0 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdFecha: { padding: '0 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdSec: { padding: '0 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdAccion: { padding: '0 16px', verticalAlign: 'middle', textAlign: 'right' },
  principal: { fontSize: text.body, fontWeight: 500, color: colors.text },
  subtitulo: { fontSize: text.meta, color: colors.textMeta, marginTop: 2 },
  empty: { padding: 32, textAlign: 'center', color: colors.textMeta },

  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.xs, fontSize: text.meta, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' },
  badgeT80: { background: colors.t80Bg, color: colors.t80Text },
  badgeVirtual: { background: '#ecfeff', color: '#0e7490' },
  badgeT90: { background: colors.t90Bg, color: colors.t90Text },
  noZona: { color: colors.textPlh, fontSize: text.sec },
  placeholder: { color: colors.textPlh },

  paginacion: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: space.xl, marginTop: space.xl },
  pageBtn: { padding: '6px 14px', background: colors.bg, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: text.body, color: colors.text },
  pageInfo: { color: colors.textSec, fontSize: text.body },
}
